import React, { useState, useEffect, useCallback } from "react";
import { Layout, Menu, Avatar, Space, Button, Tooltip, Result } from "antd";
import type { MenuProps } from "antd";
import styles from "./Layout.module.css";
import {
  MedicineBoxOutlined, UserOutlined, LogoutOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined, HistoryOutlined,
} from "@ant-design/icons";
import {
  getCurrentRole,
  getSelectedCompanyId,
  loadEnabledPagesForCompany,
  PAGES_CHANGED_EVENT,
  isPageAllowed,
} from "../utils/pagePermissions"; 
import {
  IconLayoutDashboard, IconHeartbeat, IconVirus, IconShieldCheck,
  IconUsers, IconNetwork, IconFileInvoice, IconStethoscope,
  IconStack2, IconTags, IconDna, IconChartBar,
} from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { logout } from "../api/endpoints/auth";
import { ALL_PAGES } from "../interface/Pagesconfig";

const { Header, Sider, Content } = Layout;

interface User {
  id: string; name: string;
  firstName: string | null; lastName: string | null;
  role: string; organizationName: string | null;
}

const PAGE_ICONS: Record<string, React.ReactNode> = {
  "dashboard":                  <IconLayoutDashboard size={16} stroke={2} style={{ color: "#3b82f6" }} />,
  "tpa-payer":                  <IconUsers          size={16} stroke={2} style={{ color: "#10b981" }} />,
  "network-plan":               <IconNetwork         size={16} stroke={2} style={{ color: "#f59e0b" }} />,
  "policy":                     <IconFileInvoice     size={16} stroke={2} style={{ color: "#ec4899" }} />,
  "specialty":                  <IconStethoscope     size={16} stroke={2} style={{ color: "#8b5cf6" }} />,
  "doctor":                     <IconUsers          size={16} stroke={2} style={{ color: "#06b6d4" }} />,
  "customicd":                  <IconTags            size={16} stroke={2} style={{ color: "#a855f7" }} />,
  "customactivity":             <IconStack2          size={16} stroke={2} style={{ color: "#6366f1" }} />,
  "medicalicd":                 <IconVirus           size={16} stroke={2} style={{ color: "#ef4444" }} />,
  "medicalactivity":            <IconHeartbeat       size={16} stroke={2} style={{ color: "#f43f5e" }} />,
  "icd-not-covered":            <IconVirus           size={16} stroke={2} style={{ color: "#ea580c" }} />,
  "icd-not-allowed-principal":  <IconVirus           size={16} stroke={2} style={{ color: "#dc2626" }} />,
  "icd-age-gender":             <IconVirus           size={16} stroke={2} style={{ color: "#14b8a6" }} />,
  "activity-not-covered":       <IconHeartbeat       size={16} stroke={2} style={{ color: "#d946ef" }} />,
  "activity-age-gender":        <IconHeartbeat       size={16} stroke={2} style={{ color: "#65a30d" }} />,
  "activities-not-allowed":     <IconHeartbeat       size={16} stroke={2} style={{ color: "#b91c1c" }} />,
  "activities-allowed":         <IconHeartbeat       size={16} stroke={2} style={{ color: "#16a34a" }} />,
  "activity-not-allowed-icd":   <IconHeartbeat       size={16} stroke={2} style={{ color: "#c026d3" }} />,
  "activity-pre-approvals":     <IconShieldCheck     size={16} stroke={2} style={{ color: "#2563eb" }} />,
  "activity-supporting-docs":   <IconFileInvoice     size={16} stroke={2} style={{ color: "#4f46e5" }} />,
  "activity-frequency":         <IconChartBar        size={16} stroke={2} style={{ color: "#059669" }} />,
  "activity-bundle":            <IconChartBar        size={16} stroke={2} style={{ color: "#059669" }} />,
  "claim-validation":           <IconShieldCheck     size={16} stroke={2} style={{ color: "#0284c7" }} />,
  "bulk-claim-validation":      <IconShieldCheck     size={16} stroke={2} style={{ color: "#4338ca" }} />,
  "claim-validation-history":   <IconDna             size={16} stroke={2} style={{ color: "#db2777" }} />,
};

const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser]           = useState<User | null>(null);
  const [menuItems, setMenuItems] = useState<MenuProps["items"]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const companyId = getSelectedCompanyId();
  const isAuditLogAllowed = ["Super Super Admin", "System Admin"].includes(getCurrentRole()) &&
    (getCurrentRole() === "Super Super Admin" || !!companyId);

  const getCurrentPageKey = () => {
    for (const group of ALL_PAGES) {
      const found = group.pages.find((p) => p.route === location.pathname);
      if (found) return found.key;
    }
    return null;
  };

  const currentPageKey = getCurrentPageKey();
  const isCurrentPageAllowed = currentPageKey ? isPageAllowed(currentPageKey, companyId) : true;

  const buildMenuFromEnabled = useCallback((enabled: string[]) => {
    const items: any[] = [];

    ALL_PAGES.forEach((group) => {
      const groupPages = group.pages.map((p) => ({
        ...p,
        isEnabled: enabled.includes(p.key),
      }));

      if (groupPages.length === 0) return;

      const groupKey = group.group.toLowerCase().replace(/\s+/g, "-");

      if (group.group === "Core" && groupPages.length === 1) {
        const p = groupPages[0];
        const isSelected = location.pathname === p.route;
        items.push({
          key: p.route,
          icon: PAGE_ICONS[p.key],
          label: (
            <div data-testid={`sidebar-nav-${p.key}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 4, opacity: p.isEnabled ? 1 : 0.75 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
              {!p.isEnabled && (
                <span data-testid={`sidebar-nav-off-${p.key}`} style={{ 
                  fontSize: "10px", 
                  backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.08)", 
                  color: isSelected ? "#ffffff" : "#9ca3af", 
                  padding: "1px 6px", 
                  borderRadius: "4px", 
                  fontWeight: isSelected ? 600 : 500,
                  lineHeight: "14px",
                  border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.12)",
                  flexShrink: 0,
                  marginLeft: 8
                }}>
                  OFF
                </span>
              )}
            </div>
          ),
        });
      } else {
        const GroupIcon = (group as any).icon || PAGE_ICONS[groupPages[0]?.key] || <SettingOutlined />;

        items.push({
          key: `group_${group.group}`,
          icon: GroupIcon,
          label: (
            <span data-testid={`sidebar-group-${groupKey}`}>
              {group.group}
            </span>
          ),
          children: groupPages.map((p) => {
            const isSelected = location.pathname === p.route;
            return {
              key: p.route,
              icon: PAGE_ICONS[p.key],
              label: (
                <div data-testid={`sidebar-nav-${p.key}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 4, opacity: p.isEnabled ? 1 : 0.75 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
                  {!p.isEnabled && (
                    <span data-testid={`sidebar-nav-off-${p.key}`} style={{ 
                      fontSize: "10px", 
                      backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.08)", 
                      color: isSelected ? "#ffffff" : "#9ca3af", 
                      padding: "1px 6px", 
                      borderRadius: "4px", 
                      fontWeight: isSelected ? 600 : 500,
                      lineHeight: "14px",
                      border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.12)",
                      flexShrink: 0,
                      marginLeft: 8
                    }}>
                      OFF
                    </span>
                  )}
                </div>
              ),
            };
          }),
        });
      }
    });

    return items;
  }, [location.pathname]);

  const buildMenu = useCallback(async () => {
    try {
      const coStr = sessionStorage.getItem("selectedCompany");
      if (!coStr) {
        setPermissionsLoaded(true);
        return;
      }

      const company = JSON.parse(coStr);
      const companyId = company?.id;
      if (!companyId) {
        setPermissionsLoaded(true);
        return;
      }

      const enabled = await loadEnabledPagesForCompany(companyId);
      setMenuItems(buildMenuFromEnabled(enabled));
    } catch (e) {
      console.error("DashboardLayout buildMenu Error:", e);
    } finally {
      setPermissionsLoaded(true);
    }
  }, [buildMenuFromEnabled]);

  useEffect(() => {
    const userStr = sessionStorage.getItem("user");
    if (userStr) { try { setUser(JSON.parse(userStr)); } catch {} }
    buildMenu();
  }, [buildMenu]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { companyId: string; pages: string[] } | undefined;
      if (!detail) { buildMenu(); return; }
      const currentId = getSelectedCompanyId();
      if (detail.companyId !== currentId) return;
      setMenuItems(buildMenuFromEnabled(detail.pages));
    };
    window.addEventListener(PAGES_CHANGED_EVENT, handler);
    window.addEventListener("companyChanged", buildMenu);
    return () => {
      window.removeEventListener(PAGES_CHANGED_EVENT, handler);
      window.removeEventListener("companyChanged", buildMenu);
    };
  }, [buildMenu, buildMenuFromEnabled]);

  useEffect(() => { buildMenu(); }, [location.pathname, buildMenu]);

  const handleLogout = async () => {
    try { await logout(); } catch {}
    finally { sessionStorage.clear(); navigate("/login"); }
  };

  return (
    <Layout className={styles.layout} data-testid="DashboardLayout-root">
      <Sider trigger={null} collapsible collapsed={collapsed} className={styles.sider} width={collapsed ? 80 : 260} data-testid="DashboardLayout-sider">
        <div className={styles.logo} data-testid="DashboardLayout-logo-container">
          {!collapsed ? (
            <div className={styles.logoText} data-testid="DashboardLayout-logo-expanded"><MedicineBoxOutlined className={styles.logoIcon} />QUDRA</div>
          ) : (
            <MedicineBoxOutlined className={styles.collapsedLogoIcon} data-testid="DashboardLayout-logo-collapsed" />
          )}
        </div>

        <Menu 
          theme="dark" 
          mode="inline" 
          selectedKeys={[location.pathname]} 
          items={menuItems} 
          className={styles.menu}
          onClick={({ key }) => navigate(key)}
          data-testid="DashboardLayout-sidebar-menu"
        />

        {!collapsed && isAuditLogAllowed && (
          <div style={{ padding: "10px 12px 20px", marginTop: "auto" }} data-testid="DashboardLayout-audit-log-expanded-wrapper">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => navigate("/audit-log")}
              style={{
                width: "100%", textAlign: "left",
                color: "rgba(255,255,255,0.75)",
                fontSize: 12,
                display: "flex", alignItems: "center", gap: 6,
              }}
              data-testid="DashboardLayout-audit-log-btn"
            >
              Audit Log
            </Button>
          </div>
        )}

        {collapsed && isAuditLogAllowed && (
          <div style={{ padding: "10px 0 20px", marginTop: "auto", display: "flex", justifyContent: "center" }} data-testid="DashboardLayout-audit-log-collapsed-wrapper">
            <Tooltip title="Audit Log" placement="right">
              <Button 
                type="text" 
                icon={<HistoryOutlined />} 
                onClick={() => navigate("/audit-log")}
                style={{ color: "rgba(255,255,255,0.75)", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }} 
                data-testid="DashboardLayout-audit-log-collapsed-btn"
              />
            </Tooltip>
          </div>
        )}
      </Sider>

      <Layout className={styles.mainLayout} style={{ marginLeft: collapsed ? 80 : 260 }} data-testid="DashboardLayout-main-container">
        <Header className={styles.header} data-testid="DashboardLayout-header">
          <Button 
            type="text" 
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} 
            onClick={() => setCollapsed(!collapsed)} 
            className={styles.headerButton} 
            data-testid="DashboardLayout-collapse-toggle-btn"
          />
          <Space size="large" align="center">
            <div className={styles.userSection} data-testid="DashboardLayout-user-section">
              <div className={styles.userInfo} data-testid="DashboardLayout-user-info">
                <Avatar size={40} className={styles.avatar} icon={<UserOutlined style={{ color:"#1890ff" }} />} data-testid="DashboardLayout-user-avatar" />
                <div className={styles.userText}>
                  <div className={styles.userName} data-testid="DashboardLayout-user-name">{user?.name || "User"}</div>
                  <div className={styles.userRole} data-testid="DashboardLayout-user-role">{user?.organizationName || user?.role || "employee"}</div>
                </div>
              </div>
              <div className={styles.divider} />
              <Button type="text" danger icon={<LogoutOutlined />} onClick={handleLogout} className={styles.logoutButton} data-testid="DashboardLayout-logout-btn">Logout</Button>
            </div>
          </Space>
        </Header>
        <Content className={styles.content} data-testid="DashboardLayout-content">
          {!permissionsLoaded ? null : !isCurrentPageAllowed ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
              <Result
                status="403"
                title="Access Denied"
                subTitle="This feature has been disabled for your company by the system administrator."
              />
            </div>
          ) : (
            <Outlet />
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;